import { useQueryClient } from '@tanstack/react-query';
import { GameEventType, type FinishRunResponse, type MeResponse } from '@rw/shared';
import { api } from '../lib/api';
import { setAppBadge } from '../lib/app-badge';
import { syncPushSubscription } from '../lib/push';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { vibrate } from '../lib/device';
import { formatKm, formatPace } from '../lib/format';
import { getRuntime, regionAt, useTracking, type LoadedRegion, type Runtime } from '../services/runtime';
import { useCurrentRegion } from '../stores/region';
import { useAuth } from '../stores/auth';
import { useNetwork } from '../stores/network';
import { useTerritoryLive } from '../stores/territory-live';
import { toast } from '../stores/toasts';
import { invalidateClubs, invalidateCompetitive, invalidateEvents, qk, useRegion, useRegions } from './queries';
import { MY_COLOR } from '../lib/colors';
import { t as tr } from '../i18n';
import { badgeTierName, rankName } from '../features/achievements/achievements';

interface RuntimeCtx {
  runtime: Runtime | null;
  region: LoadedRegion | null;
  /** Latest server result for a run synced in the background. */
  lastResult: FinishRunResponse | null;
}

const Ctx = createContext<RuntimeCtx>({ runtime: null, region: null, lastResult: null });

export const useRuntime = () => useContext(Ctx);

/**
 * Boots the client runtime once signed in: restores interrupted runs, keeps the
 * sync queue and realtime connection alive, and turns game events into feedback.
 */
export function RuntimeProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const token = useAuth((s) => s.token);
  const userId = useAuth((s) => s.user?.id ?? null);
  const online = useNetwork((s) => s.online);
  const { region: regionDto } = useRegion();
  const [runtime, setRuntime] = useState<Runtime | null>(null);
  const [region, setRegion] = useState<LoadedRegion | null>(null);
  const [lastResult, setLastResult] = useState<FinishRunResponse | null>(null);

  useEffect(() => {
    let alive = true;
    void getRuntime().then((rt) => alive && setRuntime(rt));
    return () => {
      alive = false;
    };
  }, []);

  // Keep this device's push subscription attached to the signed-in account.
  useEffect(() => {
    if (token) void syncPushSubscription();
  }, [token]);

  // Keep the cached profile (role, name, photo) fresh.
  useEffect(() => {
    if (!token) return;
    void api<MeResponse>('/auth/me')
      .then((me) => useAuth.getState().setUser(me))
      .catch(() => undefined);
  }, [token]);

  // Region → grid
  useEffect(() => {
    if (runtime && regionDto) setRegion(runtime.setRegion(regionDto));
  }, [runtime, regionDto]);

  // Which city: where the runner is. Located once at start (only if location is already allowed, never
  // prompting), then followed while the run screen gets GPS fixes; a city picked by hand stays until then.
  const regions = useRegions().data;
  useEffect(() => {
    if (!regions?.length || regions.length < 2) return;
    let alive = true;
    const follow = (p: { lat: number; lng: number }) => {
      const { regionId, pickedByHand } = useCurrentRegion.getState();
      if (pickedByHand) return;
      const cur = regions.find((r) => r.id === regionId);
      if (cur && regionAt([cur], p)) return;
      const here = regionAt(regions, p);
      if (here && alive) useCurrentRegion.getState().setRegionId(here.id);
    };
    void navigator.permissions
      ?.query({ name: 'geolocation' })
      .then((st) => {
        if (st.state !== 'granted' || !alive) return;
        navigator.geolocation.getCurrentPosition(
          (pos) => follow({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          () => undefined,
          { maximumAge: 10 * 60_000, timeout: 15_000 },
        );
      })
      .catch(() => undefined);
    const off = useTracking.subscribe((s, prev) => {
      if (s.position && s.position !== prev.position) follow(s.position);
    });
    return () => {
      alive = false;
      off();
    };
  }, [regions]);

  // Recover an interrupted run for this user (reload, crash, OS killed the tab).
  useEffect(() => {
    if (!runtime || !userId || !region) return;
    if (runtime.engine.snapshot.state !== 'IDLE') return;
    void runtime.store.listRuns().then((runs) => {
      const open = runs.find((r) => r.userId === userId && (r.status === 'active' || r.status === 'paused'));
      if (open && runtime.engine.snapshot.state === 'IDLE') {
        void runtime.engine.recover(open).then(() =>
          toast({ kind: 'info', title: tr('Run restored'), body: tr('Your unfinished run was recovered. Resume or finish it.') }, 6000),
        );
      }
    });
  }, [runtime, userId, region]);

  // Background sync
  useEffect(() => {
    if (!runtime || !userId) return;
    const offFinished = runtime.sync.onFinished((_run, result) => {
      setLastResult(result);
      qc.setQueryData(qk.run(result.run.id), result.run);
      void qc.invalidateQueries({ queryKey: qk.runs });
      invalidateCompetitive(qc);
    });
    const stopAuto = runtime.sync.startAuto();
    return () => {
      offFinished();
      stopAuto();
    };
  }, [runtime, userId, qc]);

  useEffect(() => {
    if (runtime && online) void runtime.sync.kick();
    if (runtime && online) runtime.realtime.nudge();
  }, [runtime, online]);

  // Realtime
  useEffect(() => {
    if (!runtime || !token) return;
    runtime.realtime.connect(token);
    const off = runtime.realtime.subscribe((msg) => {
      const me = useAuth.getState().user?.id;
      switch (msg.type) {
        case GameEventType.TERRITORY_CAPTURED: {
          const t = msg.payload.territory;
          useTerritoryLive.getState().apply(t);
          useTerritoryLive.getState().flash(t.gridKey, t.owner?.id === me ? MY_COLOR : (t.owner?.color ?? '#fff'));
          if (t.owner && t.owner.id !== me) {
            toast({ kind: 'info', title: tr('{name} captured a territory', { name: t.owner.displayName }), body: tr('Sector {key}', { key: t.gridKey }) }, 3500);
          }
          break;
        }
        case GameEventType.TERRITORY_UPDATED:
          for (const t of msg.payload.territories) useTerritoryLive.getState().apply(t);
          invalidateCompetitive(qc);
          break;
        case GameEventType.TERRITORY_LOST:
          if (msg.payload.lostBy.id === me) {
            vibrate([80, 60, 80]);
            toast({
              kind: 'lost',
              title: tr('Territory lost!'),
              body: tr('{name} took sector {key}. Take it back!', { name: msg.payload.territory.owner?.displayName ?? tr('A rival'), key: msg.payload.territory.gridKey }),
            }, 6000);
          }
          break;
        case GameEventType.PLAYER_NEARBY:
          toast({ kind: 'nearby', title: tr('{name} is nearby', { name: msg.payload.user.displayName }), body: tr('{n} sector(s) away', { n: msg.payload.cellsAway }) });
          break;
        case GameEventType.RUN_STARTED:
          if (msg.payload.user.id !== me) toast({ kind: 'info', title: tr('{name} started a run', { name: msg.payload.user.displayName }) }, 3000);
          break;
        case GameEventType.RUN_FINISHED:
          if (msg.payload.user.id !== me && msg.payload.status === 'VALID') {
            toast({ kind: 'info', title: tr('{name} finished {km} km', { name: msg.payload.user.displayName, km: formatKm(msg.payload.distanceM) }) }, 3000);
          }
          break;
        case GameEventType.LEADERBOARD_UPDATED:
          invalidateCompetitive(qc);
          break;
        case GameEventType.RUN_KUDOS: {
          const { runId, user } = msg.payload;
          void qc.invalidateQueries({ queryKey: ['run-kudos', runId] });
          void qc.invalidateQueries({ queryKey: ['runs', runId] });
          toast({ kind: 'info', title: tr('🏃‍♂️ {name} gave you kudos', { name: user.displayName }) }, 5000);
          break;
        }
        case GameEventType.RUN_COMMENT: {
          const { runId, comment } = msg.payload;
          void qc.invalidateQueries({ queryKey: ['run-comments', runId] });
          void qc.invalidateQueries({ queryKey: ['runs', runId] });
          toast({ kind: 'info', title: tr('💬 {name} commented on your run', { name: comment.user.displayName }), body: comment.body.slice(0, 120) }, 6000);
          break;
        }
        case GameEventType.COMMENT_REPLY: {
          const { runId, comment } = msg.payload;
          void qc.invalidateQueries({ queryKey: ['run-comments', runId] });
          void qc.invalidateQueries({ queryKey: ['runs', runId] });
          toast({ kind: 'info', title: tr('↩️ {name} replied to your comment', { name: comment.user.displayName }), body: comment.body.slice(0, 120) }, 6000);
          break;
        }
        case GameEventType.COMMENT_KUDOS: {
          const { runId, user } = msg.payload;
          void qc.invalidateQueries({ queryKey: ['run-comments', runId] });
          toast({ kind: 'info', title: tr('🏃‍♂️ {name} gave kudos to your comment', { name: user.displayName }) }, 5000);
          break;
        }
        case GameEventType.EVENT_UPDATE: {
          invalidateEvents(qc);
          const { kind, event, user, champion } = msg.payload;
          if (kind === 'invite')
            toast({ kind: 'level', title: tr('Invitation: {name}', { name: event.name }), body: user ? tr('{name} invited you — open Events to join.', { name: user.displayName }) : undefined }, 8000);
          if (kind === 'ended')
            toast({ kind: 'level', title: tr('{name} has ended', { name: event.name }), body: champion ? tr('🏆 Champion: {name}', { name: champion.displayName }) : tr('Results are final.') }, 9000);
          if (kind === 'updated') toast({ kind: 'info', title: tr('{name}: the rules changed', { name: event.name }), body: tr('Open the event to see the new rules.') }, 7000);
          if (kind === 'cancelled') toast({ kind: 'info', title: tr('{name} was cancelled', { name: event.name }) });
          if (kind === 'moved') toast({ kind: 'info', title: tr('{name}: the organisers moved you to another team', { name: event.name }) }, 7000);
          if (kind === 'removed') toast({ kind: 'info', title: tr('You were removed from {name}', { name: event.name }) });
          if (kind === 'approval_request') {
            void qc.invalidateQueries({ queryKey: ['admin', 'events'] });
            toast({ kind: 'info', title: tr('{name} wants to run {event}', { name: user?.displayName ?? '', event: event.name }), body: tr('Open Admin → Events to approve or decline.') }, 8000);
          }
          if (kind === 'approved') toast({ kind: 'level', title: tr('✅ {name} was approved', { name: event.name }), body: tr('Add teams and invite runners.') }, 8000);
          if (kind === 'rejected') toast({ kind: 'info', title: tr('{name} was not approved', { name: event.name }) }, 7000);
          break;
        }
        case GameEventType.RUN_IMPORTED: {
          const p = msg.payload;
          void qc.invalidateQueries({ queryKey: qk.runs });
          invalidateCompetitive(qc);
          toast(
            {
              kind: p.captured ? 'capture' : 'success',
              title: tr('Run imported from Strava · {km} km', { km: formatKm(p.distanceM) }),
              body: p.status === 'VALID' ? `+${p.xp} XP${p.captured ? ` · ${tr('{n} sector(s) captured', { n: p.captured })}` : ''}` : tr('Status: {status}', { status: tr(p.status) }),
            },
            7000,
          );
          break;
        }
        case GameEventType.CLUB_UPDATE: {
          invalidateClubs(qc);
          const { kind, club, user } = msg.payload;
          const text: Record<typeof kind, string> = {
            invite: tr('{club} invited you to join', { club: club.name }),
            join_request: tr('{name} wants to join {club}', { name: user.displayName, club: club.name }),
            approved: tr('You’re now a member of {club}', { club: club.name }),
            declined: tr('{club} declined your request', { club: club.name }),
            joined: tr('{name} joined {club}', { name: user.displayName, club: club.name }),
            removed: tr('You were removed from {name}', { name: club.name }),
            left: tr('{name} left {club}', { name: user.displayName, club: club.name }),
          };
          toast({ kind: kind === 'declined' || kind === 'removed' ? 'error' : 'success', title: text[kind] }, 5000);
          break;
        }
        case GameEventType.NOTIFICATION:
          qc.setQueryData(qk.notificationsUnread, { unread: msg.payload.unread });
          setAppBadge(msg.payload.unread);
          void qc.invalidateQueries({ queryKey: qk.notifications });
          break;
        case GameEventType.LEVEL_UP:
          if (msg.payload.user.id === me) toast({ kind: 'level', title: tr('Level {n}!', { n: msg.payload.level }), body: tr('You leveled up.') }, 6000);
          break;
        case GameEventType.RANK_UP:
          if (msg.payload.user.id === me) {
            toast({ kind: 'level', title: tr('New rank: {rank}', { rank: rankName(msg.payload.rank) }), body: tr('Keep running to climb higher.') }, 7000);
          }
          break;
        case GameEventType.BADGE_EARNED: {
          void qc.invalidateQueries({ queryKey: qk.myBadges });
          const [first] = msg.payload.badges;
          if (first) {
            toast(
              {
                kind: 'level',
                title: msg.payload.badges.length === 1 ? tr('🏅 Badge unlocked') : tr('🏅 {n} badges unlocked', { n: msg.payload.badges.length }),
                body: msg.payload.badges.map((b) => badgeTierName(b.id, b.tier)).join(' · '),
              },
              7000,
            );
          }
          break;
        }
        default:
          break;
      }
    });
    return () => {
      off();
    };
  }, [runtime, token, qc]);

  useEffect(() => {
    if (!token) runtime?.realtime.disconnect();
  }, [token, runtime]);

  // Provisional capture feedback while running + coarse progress to other players.
  useEffect(() => {
    if (!runtime) return;
    return runtime.engine.on((e) => {
      if (e.type === 'capture') {
        vibrate([60, 40, 120]);
        toast({ kind: 'capture', title: tr('Territory captured!'), body: tr('Sector {key} · confirmed when you finish', { key: e.gridKey }) });
      }
      if (e.type === 'split') {
        vibrate([200]);
        toast({ kind: 'success', title: `${tr('Km')} ${e.split.index} · ${formatPace(e.split.paceSecPerKm)} /km` }, 4000);
      }
      if (e.type === 'cell') {
        const s = runtime.engine.snapshot;
        if (s.runId && s.regionId) {
          runtime.realtime.send({ type: 'run:progress', runId: s.runId, regionId: s.regionId, gridKey: e.gridKey, distanceM: s.distanceM });
        }
      }
    });
  }, [runtime]);

  return <Ctx.Provider value={{ runtime, region, lastResult }}>{children}</Ctx.Provider>;
}
